import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  TextareaAutosize,
  Grid,
  Container,
  Card, // Import Card from Material-UI
  CardContent, // Import CardContent from Material-UI
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  FormHelperText,
  Input,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios"; // Import Axios for making HTTP requests
import NotificationMessage from "./NotificationMessage";

function HybridForm() {
  const [hybrid, setHybrid] = useState("");
  const [hybridTag, setHybridTag] = useState("");
  const [treatmentPackage, setTreatmentPackage] = useState("");
  const [hybridTagImage, setHybridTagImage] = useState(null);
  const [msrp, setMsrp] = useState("");
  const [cost, setCost] = useState("");
  const [notes, setNotes] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const teamName = localStorage.getItem("username");
  const [submittedForms, setSubmittedForms] = useState([]);
  const token = localStorage.getItem("token");
  const isFormDisabled = submittedForms.length > 0;

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const hybridOptions = ["Pioneer P2042VYHR /MSRP $375", "Other"];

  const hybridCosts = {
    "Pioneer P2042VYHR /MSRP $375": 375.0,

    // Assuming 'Others' does not have a predefined cost
  };

  const handleHybridClick = (selectedHybrid) => {
    setHybrid(selectedHybrid);
    setErrorMsg("");
    setSuccessMsg("");
    if (selectedHybrid === "Other") {
      // Reset fields for custom hybrid entry with default MSRP
      setHybridTag("");
      setTreatmentPackage("");
      setHybridTagImage(null);
      setMsrp("");
      setCost("");
    } else if (hybridOptions.includes(selectedHybrid)) {
      // Set cost from the hybridCosts map for pre-defined options
      const selectedCost = hybridCosts[selectedHybrid];
      setCost(selectedCost ? selectedCost.toString() : "");
      setHybridTag("");
      setTreatmentPackage("");
      setHybridTagImage(null);
      setMsrp("");
    }
  };

  const fetchSubmittedForms = async () => {
    try {
      const username = localStorage.getItem("username");
      const response = await axios.post(
        "/api/getCornHybridSubmittedForms",
        { username }, // Send username as part of the request body
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the Authorization header
          },
        },
      );

      if (response.status === 200) {
        // Update the state with the fetched data
        setSubmittedForms(response.data);

        // If there's a submitted form, set the hybrid state to show it as selected
        if (response.data && response.data.length > 0) {
          const submittedHybrid = response.data[0].hybrid;

          // Check if the submitted hybrid matches any of the predefined options
          const matchingOption = hybridOptions.find(
            (option) =>
              submittedHybrid.includes(option.split(" /MSRP")[0]) ||
              option === submittedHybrid,
          );

          if (matchingOption) {
            setHybrid(matchingOption);
          } else {
            // If it's a custom hybrid, set it as "Other"
            setHybrid("Other");
          }
        }
      } else {
        console.error("Failed to fetch submitted forms data");
      }
    } catch (error) {
      console.error("Error fetching submitted forms data:", error);
    }
  };

  useEffect(() => {
    // Fetch the submitted forms data for the logged-in user

    // Call the fetch function when the component mounts
    fetchSubmittedForms();
  }, []);

  const handleHybridTagImageChange = (event) => {
    setHybridTagImage(event.target.files[0]);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    // Check if hybrid is selected
    if (!hybrid) {
      setErrorMsg("Please fill in all inputs");
      return;
    }

    // Check if hybrid tag image is uploaded when "Other" is selected
    if (hybrid === "Other" && !hybridTagImage) {
      setErrorMsg("Please fill in all inputs (including hybrid tag image)");
      return;
    }

    // Check if all required fields are filled for "Other" option
    if (hybrid === "Other" && (!hybridTag || !msrp)) {
      setErrorMsg("Please fill in all inputs");
      return;
    }

    // First, upload the hybrid tag image if "Other" is selected
    if (hybrid === "Other" && hybridTagImage) {
      try {
        const imageFormData = new FormData();
        imageFormData.append("file", hybridTagImage);
        imageFormData.append("teamName", teamName);
        imageFormData.append("cropType", "corn");

        await axios.post("/api/uploadHybridTagImage", imageFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error("Error uploading hybrid tag image:", error);
        setStatusMsg("Error uploading hybrid tag image");
        showNotification("Error uploading hybrid tag image", "error");
        return;
      }
    }

    // Prepare the data to be sent to the backend
    const hybridName =
      hybrid === "Other"
        ? treatmentPackage
          ? `${hybridTag} ${treatmentPackage}`
          : hybridTag
        : hybrid;

    const formData = {
      hybrid: hybridName,
      cost: hybrid === "Other" ? parseFloat(msrp) : parseFloat(cost),
      notes,
      teamName,
    };

    try {
      // Make an HTTP POST request to your backend API using Axios
      const response = await axios.post(
        "/api/cornhybridsubmit", // Replace with your actual backend URL
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the Authorization header
          },
        },
      );

      if (response.status === 200) {
        // Form submission was successful
        fetchSubmittedForms();
        setStatusMsg("Form submitted successfully");
        setErrorMsg("");
        setSuccessMsg("Input submitted successfully!");
        showNotification("Hybrid form submitted successfully!", "success");
        // Reset form fields
        setHybrid("");
        setHybridTag("");
        setTreatmentPackage("");
        setHybridTagImage(null);
        setMsrp("");
        setCost("");
        setNotes("");
      } else {
        // Form submission failed
        setStatusMsg("Form submission failed");
        showNotification("Form submission failed", "error");
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      setStatusMsg("Form submission failed");
      showNotification("Form submission failed", "error");
    }
  };

  return (
    <Container>
      <Grid item xs={12}>
        <Typography variant="h5">Hybrids to use:</Typography>

        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {hybridOptions.map((option) => (
            <Card
              key={option}
              onClick={() => !isFormDisabled && handleHybridClick(option)}
              sx={{
                cursor: isFormDisabled ? "not-allowed" : "pointer",
                opacity: isFormDisabled ? 0.5 : 1,
                margin: "4px",
                padding: "10px 16px",
                minWidth: option === "Other" ? "150px" : "auto",
                backgroundColor: hybrid === option ? "#fa4616" : "#F5F5F5",
                border:
                  hybrid === option
                    ? "2px solid rgb(255, 255, 255)"
                    : "2px solid rgb(37, 106, 185)",
                borderRadius: "12px",
                color: hybrid === option ? "white" : "#333",
                boxShadow:
                  hybrid === option
                    ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                    : "none",
                transition: "all 0.3s ease-in-out",
                display: "flex",
                justifyContent: "center", // Center horizontally
                alignItems: "center", // Center vertically
                textAlign: "center", // Ensures text stays centered
                height: "50px", // Fixed height for better alignment
                "&:hover": {
                  backgroundColor: hybrid === option ? "#d73a12" : "#E0E0E0",
                  transform: "scale(1.05)",
                },
              }}
            >
              <CardContent
                sx={{
                  padding: "0",
                  paddingTop: "20px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ width: "100%" }}>
                  {option}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      </Grid>
      <Typography variant="h4" gutterBottom>
        My Form
      </Typography>
      {hybrid === "Other" && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 2, fontStyle: "italic" }}
        >
          <strong>How to fill in your hybrid details:</strong> Enter the
          complete hybrid tag from your seed bag (e.g., Pioneer P2042VYHR,
          DeKalb DKC110-10RIB). If your seed has a treatment package like Elite
          F7 or E7, enter it in the Treatment Package field. Then enter the MSRP
          price per bag and upload hybrid tag image.
          <br />
          <br />
          <strong>Note:</strong> You need to submit a physical copy of the
          hybrid tag as well.
        </Typography>
      )}
      <form onSubmit={handleFormSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">
              <label
                style={{
                  display: "inline-block",
                  marginRight: "16px",
                  alignContent: "center",
                }}
              >
                Hybrid Selection
              </label>
            </Typography>
          </Grid>

          {hybrid === "Other" ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="hybrid-tag-input"
                  label="Hybrid Tag"
                  variant="outlined"
                  fullWidth
                  value={hybridTag}
                  onChange={(e) => setHybridTag(e.target.value)}
                  placeholder="Enter your hybrid tag (e.g., P2042VYHR, DKC110-10RIB)"
                  required
                  disabled={isFormDisabled}
                  helperText="Enter the complete hybrid tag/name from your seed bag"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="treatment-package-input"
                  label="Treatment Package (Optional)"
                  variant="outlined"
                  fullWidth
                  value={treatmentPackage}
                  onChange={(e) => setTreatmentPackage(e.target.value)}
                  placeholder="e.g., Elite F7, E7, etc."
                  disabled={isFormDisabled}
                  helperText="Seed treatment package, if applicable"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="msrp-input"
                  label="MSRP"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={msrp}
                  onChange={(e) => {
                    setMsrp(e.target.value);
                    setCost(e.target.value);
                  }}
                  placeholder="Enter price per bag"
                  required
                  disabled={isFormDisabled}
                  helperText="$/bag"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <Input
                    id="hybrid-tag-image-input"
                    type="file"
                    onChange={handleHybridTagImageChange}
                    inputProps={{ accept: "image/*" }}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="hybrid-tag-image-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={isFormDisabled}
                      sx={{
                        padding: "12px",
                        borderStyle: "dashed",
                        borderWidth: "2px",
                        textTransform: "none",
                      }}
                    >
                      {hybridTagImage
                        ? hybridTagImage.name
                        : "Upload Hybrid Tag Image *"}
                    </Button>
                  </label>
                  <FormHelperText>
                    Upload a clear picture of your hybrid tag from the seed bag
                    (jpg, jpeg, png format)
                  </FormHelperText>
                </FormControl>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="hybrid-input"
                  label="Hybrid"
                  variant="outlined"
                  fullWidth
                  value="Pioneer P2042VYHR"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "rgba(0, 0, 0, 0.87)",
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                    },
                  }}
                  helperText="Pre-selected default hybrid"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="default-msrp-input"
                  label="MSRP"
                  variant="outlined"
                  fullWidth
                  value="$375"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "rgba(0, 0, 0, 0.87)",
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                    },
                  }}
                  helperText="$/bag"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              id="notes-input"
              label="Notes"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes"
              disabled={isFormDisabled}
            />
          </Grid>
          {!isFormDisabled && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  color: "#d32f2f",
                  fontWeight: 500,
                  fontStyle: "italic",
                  mb: 1,
                }}
              >
                ⚠️ Once submitted, selection cannot be changed
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              id="hybrid-selection-submit"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isFormDisabled}
            >
              {isFormDisabled ? "Form Already Submitted" : "Submit"}
            </Button>
            {errorMsg && (
              <Typography variant="body2" sx={{ color: "red", mt: 1 }}>
                {errorMsg}
              </Typography>
            )}
            {successMsg && (
              <Typography variant="body2" sx={{ color: "green", mt: 1 }}>
                {successMsg}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Typography variant="subtitle1" id="hybrid-status-msg">
              {statusMsg}
            </Typography>
          </Grid>
        </Grid>
      </form>
      {submittedForms.length > 0 && (
        <div>
          <strong>
            {" "}
            <Typography variant="h5" gutterBottom>
              Submitted Data
              <br></br>
            </Typography>{" "}
          </strong>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Hybrid</b>
                </TableCell>

                <TableCell>
                  <b>Cost per Bag</b>
                </TableCell>

                <TableCell>
                  <b>Notes</b>
                </TableCell>
                {/* <TableCell>
                    <b>Team Name</b>
                  </TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {submittedForms.map((form, index) => (
                <TableRow key={index}>
                  <TableCell>{form.hybrid}</TableCell>
                  <TableCell>${form.cost}</TableCell>
                  <TableCell>{form.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NotificationMessage
        open={notification.open}
        onClose={handleCloseNotification}
        message={notification.message}
        severity={notification.severity}
      />
    </Container>
  );
}

export default HybridForm;
